import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Mic, MicOff, Video, VideoOff, Radio, X, ArrowUpRight, Users } from "lucide-react";
import { useStreaming } from "@/lib/streaming";

/**
 * StreamingBubble — floating Picture-in-Picture style preview that shows
 * while the user is broadcasting but NOT on their own channel page. Lets
 * them see their local feed, toggle cam/mic, jump back to their channel,
 * and end the stream from anywhere in the app.
 */
export default function StreamingBubble() {
  const streaming = useStreaming();
  const location = useLocation();
  const nav = useNavigate();
  const videoRef = useRef(null);
  const [pos, setPos] = useState({ x: 24, y: 24 }); // distance from bottom-right
  const dragging = useRef(null);

  // Attach the shared MediaStream to our local <video> element (survives navigations)
  useEffect(() => {
    if (!streaming.isLive) return undefined;
    const el = videoRef.current;
    if (!el) return undefined;
    const detach = streaming.attachPreview(el);
    return detach;
  }, [streaming.isLive, streaming]);

  // Hide the bubble while the streamer is already on their own live channel page.
  const onOwnPage =
    streaming.isLive &&
    streaming.channelUsername &&
    (location.pathname === `/c/${streaming.channelUsername}` ||
     location.pathname === `/c/${streaming.channelUsername}/live`);

  const onMouseDown = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    dragging.current = {
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
    };
    document.body.style.userSelect = "none";
  };
  const onMouseMove = (e) => {
    if (!dragging.current) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = 260, h = 160;
    const right = Math.max(0, vw - (e.clientX - dragging.current.offsetX + w));
    const bottom = Math.max(0, vh - (e.clientY - dragging.current.offsetY + h));
    setPos({ x: Math.min(right, vw - w), y: Math.min(bottom, vh - h) });
  };
  const onMouseUp = () => {
    dragging.current = null;
    document.body.style.userSelect = "";
  };

  useEffect(() => {
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  });

  if (!streaming.isLive || onOwnPage) return null;

  return (
    <div
      className="fixed z-[95] w-[260px] bg-black border border-[color:var(--sc-pink)]/50 rounded-md shadow-2xl overflow-hidden select-none"
      style={{ right: pos.x, bottom: pos.y, boxShadow: "0 0 30px rgba(255,51,102,0.35)" }}
      data-testid="streaming-bubble"
    >
      <div
        className="px-3 py-2 flex items-center justify-between bg-gradient-to-r from-[color:var(--sc-pink)]/20 to-transparent cursor-move"
        onMouseDown={onMouseDown}
      >
        <div className="flex items-center gap-2">
          <span className="rec-dot" style={{ background: "#FF3366", boxShadow: "0 0 6px #FF3366" }} />
          <span className="text-[10px] sc-mono uppercase tracking-widest text-[color:var(--sc-pink)]">Live from anywhere</span>
        </div>
        <div className="flex items-center gap-1 text-[10px] text-zinc-400 sc-mono">
          <Users className="w-3 h-3" /> {streaming.viewerCount || 0}
        </div>
      </div>
      <video
        ref={videoRef}
        data-testid="streaming-bubble-video"
        autoPlay
        playsInline
        muted
        className="w-full h-[146px] object-cover bg-black"
      />
      <div className="px-2 py-2 flex items-center gap-1.5 border-t border-white/10">
        <button
          data-testid="streaming-bubble-cam"
          onClick={streaming.toggleCam}
          className="p-1.5 rounded-sm border border-white/10 hover:border-[color:var(--sc-cyan)]/60"
          title={streaming.camOn ? "Camera on" : "Camera off"}
        >
          {streaming.camOn ? <Video className="w-3.5 h-3.5" /> : <VideoOff className="w-3.5 h-3.5 text-[#FF3366]" />}
        </button>
        <button
          data-testid="streaming-bubble-mic"
          onClick={streaming.toggleMic}
          className="p-1.5 rounded-sm border border-white/10 hover:border-[color:var(--sc-cyan)]/60"
          title={streaming.micOn ? "Mic on" : "Mic off"}
        >
          {streaming.micOn ? <Mic className="w-3.5 h-3.5" /> : <MicOff className="w-3.5 h-3.5 text-[#FF3366]" />}
        </button>
        <button
          data-testid="streaming-bubble-goto-channel"
          onClick={(e) => { e.stopPropagation(); nav(`/c/${streaming.channelUsername}/live`); }}
          className="flex-1 px-2 py-1.5 rounded-sm bg-[color:var(--sc-cyan)]/15 border border-[color:var(--sc-cyan)]/50 text-[10px] sc-mono uppercase tracking-widest text-[color:var(--sc-cyan)] hover:bg-[color:var(--sc-cyan)]/30 flex items-center justify-center gap-1"
          title="Return to your channel"
        >
          <ArrowUpRight className="w-3 h-3" /> Channel
        </button>
        <button
          data-testid="streaming-bubble-end"
          onClick={streaming.stopStream}
          className="p-1.5 rounded-sm border border-white/10 hover:border-[color:var(--sc-pink)] hover:text-[color:var(--sc-pink)]"
          title="End stream"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
